import basketUtils from 'utils/Basket';
import orderUtils from 'utils/Order';
import ErrorsUtils from 'utils/Errors';
import localeUtils from 'utils/LanguageLocale';
import checkoutApi from 'services/api/checkout';
import locationUtils from 'utils/Location';
import store from 'store/Store';
import VenmoActions from 'actions/VenmoActions';
import OrderActions from 'actions/OrderActions';
import Actions from 'Actions';
import decorators from 'utils/decorators';
import { INTERSTICE_DELAY_MS } from 'components/Checkout/constants';
import checkoutUtils from 'utils/Checkout';
import VenmoBindings from 'analytics/bindingMethods/pages/checkout/VenmoBindings';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';

let venmoIntegration;
let venmoToken;
let tokenPromise = null;

const TYPES = {
    ENABLED: 'ENABLED',
    DISABLED: 'DISABLED',
    HIDDEN: 'HIDDEN'
};

const getText = localeUtils.getLocaleResourceFile('components/RwdCheckout/RwdCheckoutMain/locales', 'RwdCheckoutMain');

const Venmo = {
    TYPES,

    fetchAndSetupVenmoToken: async function () {
        try {
            const { token } = await checkoutApi.getVenmoToken();
            venmoToken = token;
            await Venmo.venmoSetup({ clientToken: token });
            store.dispatch(VenmoActions.setReady(true));
        } catch (errorData) {
            ErrorsUtils.collectAndValidateBackEndErrors(errorData);
            throw errorData;
        } finally {
            tokenPromise = null;
        }
    },

    prepareVenmoCheckout: function () {
        // If we already have a token, we're done
        if (venmoToken) {
            return;
        }

        if (!tokenPromise) {
            tokenPromise = Venmo.fetchAndSetupVenmoToken();
        }
    },

    checkoutWithVenmo: async function ({ defaultPayment, callback }) {
        try {
            VenmoBindings.placeOrderClick({ defaultPayment });
            const payload = await Venmo.tokenizeVenmo();
            await checkoutApi.initializeVenmoCheckout(payload);
            const placeOrderPayload = {
                originOfOrder: 'web'
            };

            if (defaultPayment) {
                placeOrderPayload.defaultPayment = defaultPayment;
            }

            const submittedDetails = await checkoutApi.placeOrder(placeOrderPayload);
            callback(submittedDetails);
        } catch (error) {
            Sephora.logger.error('Failed to initialize Venmo checkout:', error);

            if (error?.code) {
                Venmo.displayVenmoErrors(error);
            } else {
                Venmo.displayCartAndServiceErrors(error);
            }
        }
    },

    displayVenmoErrors: function (error) {
        const genericErrorMessage = getText('authorizeErrorMessage', ['Venmo']);

        VenmoBindings.modalError({ error, genericErrorMessage });

        switch (error.code) {
            // TODO VENMO: These two cases are the same for now, but we'll be adding more
            // specific errors later
            case 'FRAME_SERVICE_FRAME_CLOSED':
                store.dispatch(VenmoActions.showInlineError(genericErrorMessage));

                break;
            default:
                store.dispatch(VenmoActions.showInlineError(genericErrorMessage));

                break;
        }
    },

    displayCartAndServiceErrors: function (error) {
        const genericErrorMessage = getText('cartServiceError', ['Venmo']);
        const title = getText('error');
        const buttonText = getText('ok');

        VenmoBindings.modalError({ error, genericErrorMessage });

        switch (error.key) {
            // TODO VENMO: These two cases are the same for now, but we'll be adding more
            // specific errors later
            case 'checkout.cc.auth.generic.error':
                store.dispatch(VenmoActions.showModalError(genericErrorMessage, title, buttonText));

                break;

            default:
                store.dispatch(VenmoActions.showModalError(genericErrorMessage, title, buttonText));

                break;
        }
    },

    /**
     * Keep this function as plain as possible!
     * No promises, callback chains -> any of this logic could be treated as a
     * suspicious behavior and browser will block the new window with Venmo
     * Explanation of the issue:
     * http://stackoverflow.com/questions/2587677/avoid-browser-popup-blockers
     * @param callback
     */
    tokenizeVenmo: async function () {
        const isPickupOrder = basketUtils.isPickup();
        const amountWithCurrency = locationUtils.isCheckout()
            ? orderUtils.getPayPalAmount() || orderUtils.getCreditCardTotal()
            : basketUtils.getSubtotal(true, isPickupOrder);
        const amount = amountWithCurrency ? Number(basketUtils.removeCurrency(amountWithCurrency)) : 0;

        // Because Venmo tokenization opens a popup, this must be called
        // as a result of a user action, such as a button click.
        try {
            // https://braintree.github.io/braintree-web/current/Venmo.html#tokenize
            const payload = await venmoIntegration.tokenize({
                flow: 'checkout',
                billingAgreementDescription: 'Estimated Total: ' + amountWithCurrency,
                container: 'venmo-container',
                singleUse: false,
                amount: amount,
                currency: localeUtils.isCanada() ? 'CAD' : 'USD',
                enableShippingAddress: true,
                enableBillingAddress: true,
                headless: true,
                ...(isPickupOrder && { shippingAddressOverride: basketUtils.shippingAddressOverride() })
            });

            return payload;
        } catch (err) {
            Sephora.logger.error('Venmo tokenization failed:', err);
            throw err;
        }
    },

    triggerExpressCheckout: async function ({ isBopis }) {
        try {
            Storage.local.setItem(LOCAL_STORAGE.VENMO_FLOW, true);

            store.dispatch(Actions.showInterstice(true));

            const payload = await Venmo.tokenizeVenmo();
            await checkoutApi.initializeVenmoCheckout(payload);
            Venmo.goToCheckoutPage(isBopis);
        } catch (error) {
            Storage.local.setItem(LOCAL_STORAGE.VENMO_FLOW, false);
            store.dispatch(Actions.showInterstice(false));
            Venmo.displayCartAndServiceErrors(error);
            Sephora.logger.error('Failed to initialize Venmo checkout:', error);
        }
    },

    goToCheckoutPage: async function (isBopis) {
        return checkoutUtils
            .initializeCheckout({
                isVenmoFlow: true,
                ropisCheckout: isBopis
            })
            .then(() => checkoutUtils.initOrderSuccess(true, isBopis))
            .catch(checkoutUtils.initOrderFailure);
    },

    braintreeCreateSuccessCallback: async function ({ clientInstance }) {
        const braintreeVenmo = await import(/* webpackChunkName: "priority" */ 'BraintreeVenmo');

        // https://braintree.github.io/braintree-web/current/module-braintree-web_venmo.html#.create
        braintreeVenmo
            .create({
                client: clientInstance,
                useRedirectForIOS: false,
                requireManualReturn: false,
                allowDesktopWebLogin: true,
                mobileWebFallBack: true,
                paymentMethodUsage: 'single_use'
            })
            .then(venmoInstance => {
                venmoIntegration = venmoInstance;
            })
            .catch(err => {
                Sephora.logger.error('Error creating Braintree Venmo:', err);
            });
    },

    initializeVenmoCheckout: function () {
        decorators
            .withInterstice(
                checkoutApi.initializePaymentGroup,
                INTERSTICE_DELAY_MS
            )({ paymentGroupType: 'VenmoPaymentGroup' })
            .then(() => {
                checkoutApi.getOrderDetails(orderUtils.getOrderId()).then(order => {
                    store.dispatch(OrderActions.updateOrder(order));
                    // For Afterpay, Klarna, and Paze:
                    // - When the payment group is initialized, it returns isComplete: true.
                    // - The subsequent Order Details API call also includes header.isComplete: true.
                    // - This enables the Place Order button.
                    //
                    // PayPal behaves differently:
                    // - As soon as the user clicks the radio button, the PayPal window opens
                    //   (so no need to enable the Place Order button).
                    // - When the payment group is initialized, it returns isComplete: false.
                    //
                    // Venmo is a special case:
                    // - The user experience is similar to Afterpay/Klarna/Paze,
                    // - But the API behaves like PayPal (returning isComplete: false
                    //   even though it should be true for consistency), so we have to enable
                    //   the Place Order button manually.
                    store.dispatch(OrderActions.togglePlaceOrderDisabled(false));
                    this.prepareVenmoCheckout();
                });
            })
            .catch(err => {
                store.dispatch(Actions.showInterstice(false));
                Sephora.logger.error(err);
            });
    },

    venmoSetup: async function ({ clientToken }) {
        const client = await import(/* webpackChunkName: "components" */ 'BraintreeClient');

        // https://braintree.github.io/braintree-web/current/module-braintree-web_client.html#.create
        client
            .create({ authorization: clientToken })
            .then(clientInstance => {
                Venmo.braintreeCreateSuccessCallback({ clientInstance });
            })
            .catch(clientErr => {
                Sephora.logger.error('Error creating Braintree Client:', clientErr);
            });
    }
};

export default Venmo;
